import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "../lib/api";
import AdminLayout from "./AdminLayout";
import { AuthedVideo } from "../components/AuthedMedia";
import { useAppT } from "../components/home/i18n";
import { CheckCircle2, ShieldAlert } from "lucide-react";

export default function AdminMediaReview() {
  const { a } = useAppT();
  const [status, setStatus] = useState("pending");
  const [videos, setVideos] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => { const { data } = await API.get("/admin/media/videos", { params: { status } }); setVideos(data.videos); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const review = async (id, decision, n = "") => {
    try { await API.post(`/admin/media/${id}/video-review`, { decision, note: n }); toast.success(decision === "approved" ? a.adminVideo.videoReleased : a.adminVideo.videoRejected); setRejectFor(null); setNote(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-serif text-3xl text-[#173A2C]">{a.adminVideo.title}</h1>
        <div className="flex gap-1 rounded-full border border-[#E8E4DB] bg-white p-1">
          {["pending", "approved", "rejected"].map((s) => (
            <button key={s} data-testid={`vidfilter-${s}`} onClick={() => setStatus(s)} className={`rounded-full px-3 py-1 text-sm ${status === s ? "bg-[#1F4A38] text-white" : "text-[#5C5855]"}`}>{a.adminVideo.filters[s]}</button>
          ))}
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 rounded-sm bg-[#C4A46B]/10 p-3 text-xs text-[#8a6f38]">
        <ShieldAlert className="h-4 w-4 shrink-0" /> {a.adminVideo.hint}
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="video-review-list">
        {videos.length === 0 && <div className="text-sm text-[#5C5855]">{a.adminVideo.noVideos}</div>}
        {videos.map((v) => (
          <div key={v.id} data-testid={`review-video-${v.id}`} className="rounded-md border border-[#E8E4DB] bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="font-serif text-base text-[#173A2C]">{a.adminVideo.kinds[v.kind] || v.kind}</div>
              <span className="text-xs text-[#5C5855]">{v.candidate_alias}</span>
            </div>
            <div className="mt-2"><AuthedVideo path={`/media/${v.id}/file`} testid={`admin-video-${v.id}`} className="h-52 w-full rounded-sm bg-black object-contain" /></div>
            <div className="mt-1 text-[11px] text-[#8a8580]">{v.duration_s ? `${Math.round(v.duration_s)}s` : ""}{v.size ? ` · ${(v.size / 1024 / 1024).toFixed(1)} MB` : ""} · {a.adminVideo.statusWord} {v.review_status}</div>
            {status === "pending" && (
              <div className="mt-3 flex gap-2">
                <button data-testid={`approve-video-${v.id}`} onClick={() => review(v.id, "approved")} className="inline-flex items-center gap-1 rounded-full bg-[#2E6B4B] px-4 py-1.5 text-xs font-semibold text-white"><CheckCircle2 className="h-3.5 w-3.5" /> {a.adminVideo.approve}</button>
                <button data-testid={`reject-video-${v.id}`} onClick={() => setRejectFor(v.id)} className="rounded-full border border-[#8B3A3A] px-4 py-1.5 text-xs font-semibold text-[#8B3A3A]">{a.adminVideo.reject}</button>
              </div>
            )}
            {v.review_note && <div className="mt-2 text-xs text-[#8B3A3A]">{a.adminVideo.notePrefix} {v.review_note}</div>}
            {rejectFor === v.id && (
              <div className="mt-3 rounded-sm border border-[#E8E4DB] p-3">
                <textarea data-testid={`video-reject-note-${v.id}`} rows="2" value={note} onChange={(e) => setNote(e.target.value)} placeholder={a.adminVideo.rejectPlaceholder} className="w-full rounded-sm border border-[#E8E4DB] px-3 py-2 text-sm outline-none focus:border-[#1F4A38]" />
                <div className="mt-2 flex gap-2">
                  <button data-testid={`video-reject-submit-${v.id}`} onClick={() => note.trim() ? review(v.id, "rejected", note) : toast.error(a.adminVideo.noteRequired)} className="rounded-sm bg-[#8B3A3A] px-3 py-1.5 text-xs font-semibold text-white">{a.adminVideo.confirmReject}</button>
                  <button onClick={() => { setRejectFor(null); setNote(""); }} className="rounded-sm border border-[#E8E4DB] px-3 py-1.5 text-xs">{a.common.cancel}</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
